import { z } from 'zod';

export const UpwindResourceKeyTypeSchema = z.enum([
  'upwind_asset_id',
  'cloud_account_id',
]);

export const UpwindSelectableResourceKindSchema = z.enum([
  'UPWIND_ASSET',
  'CLOUD_ACCOUNT',
]);

export const UpwindSelectableResourceSchema = z.object({
  id: z.string().min(1),
  kind: UpwindSelectableResourceKindSchema,
  resourceKey: z.string().min(1),
  name: z.string().min(1),
  nativeType: z.string().min(1),
  cloudAccountId: z.string().nullable().optional(),
  cloudPlatform: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
});

export const UpwindSelectableResourcesQuerySchema = z.object({
  search: z.string().trim().optional(),
});

export const UpwindSelectableResourcesResponseSchema = z.object({
  items: z.array(UpwindSelectableResourceSchema),
});

export const UpwindApplicationMappingSchema = z.object({
  applicationId: z.string().min(1),
  resource: UpwindSelectableResourceSchema,
});

export const UpwindApplicationMappingsSchema = z.array(
  UpwindApplicationMappingSchema,
);

const LegacyUpwindApplicationMappingSchema = z.object({
  applicationId: z.string().min(1),
  resourceKeyType: UpwindResourceKeyTypeSchema,
  resourceKey: z.string().min(1),
  label: z.string().optional(),
});

const toSelectableResourceFromLegacyMapping = ({
  mapping,
}: {
  mapping: z.infer<typeof LegacyUpwindApplicationMappingSchema>;
}): UpwindSelectableResource => {
  const isAsset = mapping.resourceKeyType === 'upwind_asset_id';

  return {
    id: `${mapping.resourceKeyType}:${mapping.resourceKey}`,
    kind: isAsset ? 'UPWIND_ASSET' : 'CLOUD_ACCOUNT',
    resourceKey: mapping.resourceKey,
    name: mapping.label?.trim() || mapping.resourceKey,
    nativeType: isAsset ? 'Asset' : 'Cloud account',
    cloudAccountId: isAsset ? null : mapping.resourceKey,
    cloudPlatform: null,
    region: null,
  };
};

export const normalizeUpwindApplicationMapping = (
  value: unknown,
): UpwindApplicationMapping | null => {
  const parsedCurrent = UpwindApplicationMappingSchema.safeParse(value);
  if (parsedCurrent.success) {
    return parsedCurrent.data;
  }

  const parsedLegacy = LegacyUpwindApplicationMappingSchema.safeParse(value);
  if (!parsedLegacy.success) {
    return null;
  }

  return {
    applicationId: parsedLegacy.data.applicationId,
    resource: toSelectableResourceFromLegacyMapping({
      mapping: parsedLegacy.data,
    }),
  };
};

export const parseUpwindApplicationMappings = (
  value: unknown,
): UpwindApplicationMapping[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((mapping) => normalizeUpwindApplicationMapping(mapping))
    .filter((mapping): mapping is UpwindApplicationMapping => mapping !== null);
};

export type UpwindApplicationMapping = z.infer<
  typeof UpwindApplicationMappingSchema
>;
export type UpwindResourceKeyType = z.infer<typeof UpwindResourceKeyTypeSchema>;
export type UpwindSelectableResource = z.infer<
  typeof UpwindSelectableResourceSchema
>;
export type UpwindSelectableResourceKind = z.infer<
  typeof UpwindSelectableResourceKindSchema
>;
